import React from 'react';
import { getCategoryColor } from '../../utils/categories';
import {
  collectorFacts,
  collectorReverseArt,
  collectorModificationMap,
  type CollectorKind,
  collectorRecognition,
  collectorTemplate,
  collectorPresentation,
} from '@bilt/domain/collectorTemplate';
import { publicCardFacts, passportKind, passportEventDate } from '@bilt/domain/passportRegistry';
import { makeCardRecord } from '@bilt/domain/cardRecord';
import type { PassportCard } from '../../services/api/passportCards';
export default function PassportTypeBack({
  card,
  demo,
  compact = false,
}: {
  card: PassportCard;
  demo: boolean;
  compact?: boolean;
}) {
  const record = makeCardRecord(card, demo),
    kind = card.subject_type,
    adapter = passportKind(kind);
  const facts = card.subject
    ? compact
      ? collectorFacts(kind, 'back', card.subject.facts)
      : publicCardFacts(kind, card.subject.facts)
    : [];
  const recognition = collectorRecognition(card.subject?.card_summary?.badges);
  const fields = (keys?: string[]) =>
    facts
      .filter((f) => !keys || keys.includes(f.key))
      .slice(0, compact ? 4 : undefined)
      .map((f) => (
        <div key={f.key}>
          <dt>{f.key.replaceAll('_', ' ')}</dt>
          <dd>
            {kind === 'event' && ['start_at', 'end_at'].includes(f.key)
              ? passportEventDate(f.value, String(card.subject?.facts?.timezone || 'UTC'), true)
              : f.value}
          </dd>
        </div>
      ));
  const receipt = (
    <section className="pe-receipt">
      <span className="pe-kicker">{demo ? 'Sample receipt' : 'Collection record'}</span>
      <p>{record.state}</p>
      {card.earned_at && !demo && <time>{card.earned_at.split(/[T ]/)[0]}</time>}
    </section>
  );
  if (!card.subject)
    return (
      <section className="pe-back pe-private">
        <h3>Collected memory</h3>
        <p>Subject details are no longer public.</p>
        {!compact && receipt}
      </section>
    );
  if (compact) {
    const presentation = collectorPresentation(
      kind,
      card.subject.facts,
      card.subject.card_summary?.badges,
      card.design.story
    );
    const art = collectorReverseArt[kind as CollectorKind];
    const modMap = collectorModificationMap(card.subject.card_summary?.modification_systems);
    const provenance = record.facts.filter(
      (f) =>
        ['Collection method', 'Collection opens', 'Collection closes'].includes(f.label) &&
        f.value !== '-'
    );
    return (
      <section
        className={`pe-back pe-back-${kind} pe-back-compact ct-record-sheet`}
        data-record-kind={kind}
        data-density={presentation.density}
      >
        <header className="ct-sheet-heading">
          <div>
            <span className="pe-kicker">{presentation.heading}</span>
            <h3>{card.title}</h3>
          </div>
          {art && (
            <svg viewBox="0 0 80 80" fill="none" aria-hidden="true">
              <path d={art.path} stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
            </svg>
          )}
        </header>
        {kind === 'vehicle' && modMap.rows.length > 0 && (
          <section className="ct-mod-map" aria-label="Modifications by system">
            <div className="ct-mod-map__heading">
              <span className="pe-kicker">Build systems</span>
              <span>
                {modMap.systems} systems · {modMap.total} parts
              </span>
            </div>
            <div className="ct-mod-map__strip" aria-hidden="true">
              {modMap.rows.map((row) => (
                <i
                  key={row.category}
                  style={{ flex: row.count, background: getCategoryColor(row.category) }}
                />
              ))}
            </div>
            <dl>
              {modMap.rows.map((row) => (
                <div key={row.category}>
                  <dt>
                    <i style={{ background: getCategoryColor(row.category) }} />
                    {row.category}
                  </dt>
                  <dd>{row.count}</dd>
                  <span className="ct-mod-map__track" aria-hidden="true">
                    <i
                      style={{
                        width: `${(row.count / modMap.total) * 100}%`,
                        background: getCategoryColor(row.category),
                      }}
                    />
                  </span>
                </div>
              ))}
            </dl>
          </section>
        )}
        {presentation.back.length > 0 && (
          <dl className={`ct-reverse-facts ct-reverse-${kind}`}>
            {presentation.back.map((f) => (
              <div key={f.key} data-field={f.key}>
                <dt>{f.label}</dt>
                <dd>
                  {kind === 'event' && f.key === 'end_at'
                    ? passportEventDate(
                        f.value,
                        String(card.subject?.facts?.timezone || 'UTC'),
                        true
                      )
                    : f.value}
                </dd>
              </div>
            ))}
          </dl>
        )}
        {!presentation.back.length && !presentation.reverseStory && !modMap.rows.length && (
          <p className="ct-sheet-empty">Additional details have not been shared.</p>
        )}
        {presentation.reverseRecognition.length > 0 && (
          <section className="pe-recognition-record">
            <span className="pe-kicker">Recognition</span>
            <ul>
              {presentation.reverseRecognition.map((r) => (
                <li key={r.id}>
                  <strong>{r.title}</strong>
                  {r.issuer && <small>{r.issuer}</small>}
                </li>
              ))}
            </ul>
          </section>
        )}
        {presentation.reverseStory && (
          <section className="ct-reverse-story">
            <span className="pe-kicker">{kind === 'vehicle' ? 'Build story' : 'Source notes'}</span>
            <p>{presentation.reverseStory}</p>
          </section>
        )}
        {kind !== 'vehicle' && provenance.length > 0 && (
          <dl className="ct-sheet-provenance">
            {provenance.slice(0, 2).map((f) => (
              <div key={f.label}>
                <dt>{f.label}</dt>
                <dd>{f.value}</dd>
              </div>
            ))}
          </dl>
        )}
        {card.earned_at && !demo && (
          <p className="pe-earned-evidence">
            {record.state} · {card.earned_at.split(/[T ]/)[0]}
          </p>
        )}
        <p className="pe-record-issuer">{record.issuer}</p>
      </section>
    );
  }
  return (
    <section
      className={`pe-back pe-back-${kind} ${compact ? 'pe-back-compact' : ''}`}
      data-record-kind={kind}
    >
      <header>
        <span className="pe-kicker">
          {collectorTemplate(kind)?.heading || adapter?.back.replaceAll('_', ' ') || 'Provenance'}
        </span>
        <h3>{card.title}</h3>
        {!compact &&
          card.subject.subtitle &&
          card.subject.subtitle !== card.subject.facts?.story && <p>{card.subject.subtitle}</p>}
      </header>
      {kind === 'vehicle' ? (
        <>
          <dl className="pe-build-record">
            {fields(['engine', 'transmission', 'drivetrain', 'chassis'])}
          </dl>
        </>
      ) : kind === 'event' ? (
        <>
          <dl className="pe-timeline">
            {fields(
              compact
                ? ['end_at', 'timezone', 'host', 'category']
                : ['start_at', 'end_at', 'timezone', 'host', 'venue', 'city', 'category']
            )}
          </dl>
        </>
      ) : kind === 'club' ? (
        <>
          <div className="pe-community-heading">Community profile</div>
          <dl className="pe-community-facts">{fields()}</dl>
        </>
      ) : kind === 'shop' ? (
        <>
          <dl className="pe-service-record">{fields()}</dl>
        </>
      ) : kind === 'place' ? (
        <div className="pe-postcard-back">
          <dl>{fields(['category', 'offering', 'exhibition', 'access'])}</dl>
          <address>{card.subject.facts?.address || card.subject.facts?.city || '-'}</address>
          {card.subject.facts?.operator && <p>Operated by {card.subject.facts.operator}</p>}
        </div>
      ) : kind === 'booth' ? (
        <>
          <div className="pe-exhibitor-heading">Exhibitor profile</div>
          <dl className="pe-exhibitor-facts">{fields()}</dl>
        </>
      ) : kind === 'activation' ? (
        <>
          <div className="pe-experience-heading">Participation record</div>
          <dl className="pe-experience-facts">{fields()}</dl>
        </>
      ) : kind === 'destination' ? (
        <>
          <dl className="pe-route-record">{fields()}</dl>
        </>
      ) : kind === 'checkpoint' ? (
        <>
          <div className="pe-criterion">
            <span>Published criterion</span>
            <p>{card.subject.facts?.criterion || 'See program requirements'}</p>
          </div>
          <dl className="pe-checkpoint-facts">
            {fields(['position', 'campaign_title', 'optional'])}
          </dl>
        </>
      ) : kind === 'commemorative' ? (
        <>
          <div className="pe-colophon">
            <p>
              {card.subject.facts?.story || card.design.story || 'An authored Bilt collectible.'}
            </p>
            <dl>{fields(['occasion', 'issuer', 'credits'])}</dl>
          </div>
        </>
      ) : (
        <dl>{fields()}</dl>
      )}
      {recognition.length > 0 && (
        <section className="pe-recognition-record">
          <span className="pe-kicker">Recognition</span>
          <ul>
            {recognition.slice(0, compact ? 2 : undefined).map((r) => (
              <li key={r.id}>
                <strong>{r.title}</strong>
                {r.issuer && <small>{r.issuer}</small>}
              </li>
            ))}
          </ul>
        </section>
      )}
      {compact && <p className="pe-record-issuer">{record.issuer}</p>}
      {!compact && receipt}
    </section>
  );
}
